/* eslint-disable custom-rules/require-client-param */
import type { Chain, DynamicClient } from '@dynamic-labs-sdk/client';
import {
  createWalletProviderEventEmitter,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
} from '@dynamic-labs-sdk/client/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import { isHex, getAddress as viemGetAddress } from 'viem';

import type {
  EIP1193Provider,
  EvmWalletProvider,
} from '../../EvmWalletProvider.types';
import { evmExecuteCheckoutTransaction } from '../evmExecuteCheckoutTransaction';
import { evmTransferAmount } from '../evmTransferAmount';
import { addNetwork } from './addNetwork';
import { getActiveNetworkId } from './getActiveNetworkId/getActiveNetworkId';
import { switchActiveNetwork } from './switchActiveNetwork';

type CreateWalletProviderFromEIP1193ProviderParams = {
  displayName: string;
  eip1193: EIP1193Provider;
  icon: string;
};

export const createWalletProviderFromEIP1193Provider = (
  { eip1193, icon, displayName }: CreateWalletProviderFromEIP1193ProviderParams,
  client: DynamicClient
): EvmWalletProvider => {
  const chain: Chain = 'EVM';

  const walletProviderType = WalletProviderEnum.BrowserExtension;

  const key = formatWalletProviderKey({
    chain,
    displayName,
    walletProviderType,
  });

  const addNetworkWrapped: EvmWalletProvider['addNetwork'] = async ({
    networkData,
  }) => addNetwork({ eip1193, networkData });

  const connect: EvmWalletProvider['connect'] = async () => {
    const addresses = (await eip1193.request({
      method: 'eth_requestAccounts',
      params: [],
    })) as string[];

    const formattedAddresses = addresses.map((address) =>
      viemGetAddress(address)
    );

    return {
      addresses: formattedAddresses.map((address) => ({
        address,
      })),
    };
  };

  const disconnect: EvmWalletProvider['disconnect'] = async () => {
    await eip1193.request({
      method: 'wallet_revokePermissions',
      params: [
        {
          eth_accounts: {},
        },
      ],
    });
  };

  const getConnectedAddresses: EvmWalletProvider['getConnectedAddresses'] =
    async () => {
      const addresses = (await eip1193.request({
        method: 'eth_accounts',
        params: [],
      })) as string[];

      const formattedAddresses = addresses.map((address) =>
        viemGetAddress(address)
      );

      return {
        addresses: formattedAddresses,
      };
    };

  const signMessage: EvmWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
  }) => {
    const params = [message];

    if (walletAccount) {
      params.push(walletAccount.address);
    }

    const signedMessage = await eip1193.request({
      method: 'personal_sign',
      params,
    });

    return {
      signature: signedMessage as string,
    };
  };

  const { getEventEmitter, cleanupEventEmitter } =
    createWalletProviderEventEmitter({
      removeEventListeners: () => {
        eip1193.removeAllListeners();
      },

      setupEventListeners: ({
        handleAccountsChanged,
        handleDisconnected,
        handleNetworkChanged,
      }) => {
        eip1193.on('accountsChanged', (addresses) => {
          handleAccountsChanged({ addresses });
        });

        eip1193.on('disconnected', () => {
          handleDisconnected();
        });

        eip1193.on('chainChanged', (chain) => {
          const networkId = isHex(chain)
            ? parseInt(chain, 16).toString()
            : chain.toString();

          handleNetworkChanged({ networkId });
        });
      },
    });

  const terminate: EvmWalletProvider['terminate'] = async () => {
    cleanupEventEmitter();
  };

  return {
    addNetwork: addNetworkWrapped,
    chain,
    connect,
    disconnect,
    get events() {
      return getEventEmitter();
    },
    executeCheckoutTransaction: (args) =>
      evmExecuteCheckoutTransaction(args, client),
    getActiveNetworkId: () => getActiveNetworkId({ eip1193 }),
    getConnectedAddresses,
    groupKey: formatWalletProviderGroupKey(displayName),
    key,
    metadata: { displayName, icon },
    request: eip1193.request.bind(eip1193),
    signMessage,
    switchActiveNetwork: ({ networkId }) =>
      switchActiveNetwork({
        client,
        eip1193,
        networkId,
        walletProviderKey: key,
      }),
    terminate,
    transferAmount: (args) => evmTransferAmount(args, client),
    walletProviderType,
  };
};
